import * as React from 'react';

import lodashStartcase from 'lodash.startcase';
import { addYears, format } from 'date-fns';

import FormRow from 'components/shared/forms/FormRow';
import BooleanOption from 'components/shared/forms/BooleanOption';
import ReactSelect from 'components/shared/forms/ReactSelect';
import QuarterPicker from 'components/shared/forms/QuarterPicker';
import numeral from 'numeral';
import Context from '../Context';
import RadioButtonGroup from '../../../../shared/forms/RadioButtonGroup';
import DataFrequencyPicker from './DataFrequencyPicker';
import CommentaryPicker from './CommentaryPicker';
import RelationshipManager from './RelationshipManager';
import { Bookkeeping } from 'javascript/models/Bookkeeping';

const Information = ({ reportTemplates }) => {
  const context = React.useContext(Context);
  const { portfolio } = context.state;
  const { reviewCommentaryTypeOptions, reviewFrequencyOptions } = context.props;

  const {
    answers,
    bookkeepingEnabled,
    client,
    consolidated,
    combined,
    dataFrequencyMonths,
    status,
    fee,
    firstReviewDate,
    quantOnly,
    reportTemplateIds,
    reviewCommentaryType,
    reviewFrequency,
    reviewPeriodYears,
    useLookthroughAssetClass,
  } = portfolio;

  const defaultReportTemplates = reportTemplates.filter((template) => reportTemplateIds.includes(template.value));
  const [selectedCommentaryType, setSelectedCommentaryType] = React.useState(reviewCommentaryType);

  const [selectedFeeType, setSelectedFeeType] = React.useState(context.props.feeType ? context.props.feeType : 1);

  const defaultFee = fee ? fee : client.emapFee;

  const handleFeeTypeChange = (newValue: string) => {
    setSelectedFeeType(newValue);
  };

  const feeTypeSelector = () => {
    if (!context.props.feeTypeOptions) {
      return;
    }
    const transformedArray = Object.entries(context.props.feeTypeOptions).map(([key, value]) => ({
      label: key,
      value,
    }));
    return (
      <RadioButtonGroup
        options={transformedArray}
        name="portfolio[fee_type]"
        selectedValue={selectedFeeType}
        onChange={handleFeeTypeChange}
      />
    );
  };

  const activePeriods = ['1', '2', '3', '4'].map((period) => (
    <div className="pretty p-icon" key={period}>
      <input type="hidden" name={`portfolio[q${period}]`} value="0" />
      <input type="checkbox" name={`portfolio[q${period}]`} value="1" defaultChecked={portfolio[`q${period}`]} />
      <div className="state p-blue">
        <i className="icon-tick icon icon-push-down-small" />
        <label className="text-white">Q{period}</label>
      </div>
    </div>
  ));

  const reviewTimePeriods = [1, 3, 5].map((period) => (
    <div className="pretty p-icon" key={period}>
      <input
        type="radio"
        name="portfolio[review_period_years]"
        value={period}
        defaultChecked={reviewPeriodYears === period}
      />
      <div className="state p-blue">
        <i className="icon-tick icon icon-push-down-small" />
        <label className="text-white">
          {period} {period === 1 ? 'year' : 'years'}
        </label>
      </div>
    </div>
  ));

  const reviewTemplateSelect = (
    <ReactSelect
      theme="dark"
      name="portfolio[report_template_ids][]"
      id="report_template_ids"
      isClearable
      isMulti
      options={reportTemplates}
      defaultValues={defaultReportTemplates}
    />
  );

  const reviewCommentaryTypeSelect = reviewCommentaryTypeOptions.map((option, index) => {
    const handleSelect = () => {
      setSelectedCommentaryType(option.value);
    };
    return (
      <div className="col-sm-1-2" key={option.value}>
        <div className="pretty p-icon">
          <input
            type="radio"
            name="portfolio[review_commentary_type]"
            value={option.value}
            checked={option.value === selectedCommentaryType}
            onChange={handleSelect}
          />
          <div className="state p-blue">
            <i className="icon-tick icon icon-push-down-small" />
            <label className="text-white">{option.label}</label>
          </div>
        </div>
      </div>
    );
  });

  const consolidatedString = 'This portfolio is marked as consolidated, so you can only assign consolidated templates.';
  const nonConsolidatedString = "This portfolio is not consolidated, so you can't select consolidated templates.";

  const feeByType = () => {
    let feeString = `Standard EMAP fee for this client is ${numeral(client.emapFee).format('0,0.00')}`;
    if (consolidated) {
      feeString = `Consolidated fees for this client are ${numeral(client.consolidatedFeeTier1).format(
        '0,0.00',
      )} / ${numeral(client.bookkeepingFee).format('0,0.00')} / ${numeral(client.consolidatedFeeTier3).format(
        '0,0.00',
      )}`;
    } else if (combined) {
      feeString = `Combined fee for this client is ${numeral(client.combinedFee).format('0,0.00')}`;
    } else if (status === 'component_part') {
      feeString = `Child combined fee for this client is ${numeral(client.combinedComponentFee).format('0,0.00')}`;
    }
    return feeString;
  };

  const commentaryPicker = (
    <FormRow note="" className="col-mc-1-1">
      <CommentaryPicker portfolio={portfolio} />
    </FormRow>
  );

  return (
    <>
      <RelationshipManager />
      <fieldset className="form__fieldset">
        <h4 className="form__fieldset-label">Review Information</h4>
        <div className="frow frow--gutters">
          <FormRow note="" className="col-mc-1-1">
            <label htmlFor="" className="form__label">
              Review Frequency
            </label>
            <ReactSelect
              theme="dark"
              name="portfolio[review_frequency]"
              id="review_frequency"
              isClearable
              options={reviewFrequencyOptions}
              defaultValue={{ label: lodashStartcase(reviewFrequency), value: reviewFrequency }}
            />
          </FormRow>
          <FormRow note="" className="col-mc-1-1">
            <label htmlFor="" className="form__label">
              Active Periods
            </label>
            {activePeriods}
          </FormRow>
          <FormRow
            note="The quarter that the first review should be created. This will supersede the active periods until after
            this date."
            className="col-mc-1-1"
          >
            <QuarterPicker
              defaultValue={firstReviewDate}
              label="First Review Date"
              id="first_review_date"
              name="portfolio[first_review_date]"
              optionCount={24}
              startDate={format(addYears(new Date(), -4), 'YYYY-MM-DD')}
              theme="dark"
              reverseDates
            />
          </FormRow>
          <FormRow
            className="col-mc-1-1"
            note="The number of years the review will cover. Changing this will not affect any reviews already created. If
            you need to change an existing review, you will need to delete the current review and create a new one."
          >
            <label htmlFor="" className="form__label">
              Review Time Period
            </label>
            {reviewTimePeriods}
          </FormRow>
          <FormRow note="" className="col-mc-1-1">
            <label htmlFor="" className="form__label">
              Quant Only Reviews
            </label>
            <BooleanOption name="portfolio[quant_only]" defaultValue={quantOnly} />
          </FormRow>
          <FormRow note="" className="col-mc-1-1">
            <label htmlFor="" className="form__label">
              Reviews should have standard questions
            </label>
            <BooleanOption name="portfolio[answers]" defaultValue={answers} />
          </FormRow>
          <FormRow
            className="col-mc-1-1"
            note="When calculating the overall asset allocation of a portfolio with holdings level data, Mosaic will use
            the headline asset class of the various assets that the portfolio holds. In cases where the portfolio invests
            in a small number of multi-asset funds, the lookthrough asset allocation of the assets may be more
            appropriate."
          >
            <label htmlFor="" className="form__label">
              Use Lookthrough Asset Class
            </label>
            <BooleanOption name="portfolio[use_lookthrough_asset_class]" defaultValue={useLookthroughAssetClass} />
          </FormRow>

          <FormRow note="" className="col-mc-1-1">
            <label htmlFor="" className="form__label">
              Bookeeping Enabled
            </label>
            <BooleanOption name="portfolio[bookkeeping_enabled]" defaultValue={bookkeepingEnabled} />
          </FormRow>

          <FormRow note="" className="col-mc-1-1">
            <DataFrequencyPicker defaultChecked={dataFrequencyMonths} />
          </FormRow>
          <FormRow note={feeTypeSelector()}>
            <label htmlFor="fee" className="form__label">
              Review Fee
              <span>{feeByType()}</span>
            </label>
            <input
              type="number"
              step="any"
              name="portfolio[fee]"
              defaultValue={defaultFee}
              hidden={selectedFeeType == 1}
              disabled={selectedFeeType == 1}
            />
          </FormRow>
          <FormRow note={consolidated ? consolidatedString : nonConsolidatedString} className="col-mc-1-1">
            <label htmlFor="fee" className="form__label">
              Report Templates
            </label>
            {reportTemplates.length > 0 && reviewTemplateSelect}
          </FormRow>
          <FormRow
            note="Standard commentary is auto-generated commentary that can be edited. Longform commentary is written
            completely by the review writer, the writer has more control over the style and it can be as long as needed."
            className="col-mc-1-1"
          >
            <label htmlFor="" className="form__label">
              Review Commentary Type
            </label>
            <div className="frow frow--gutters">{reviewCommentaryTypeSelect}</div>
          </FormRow>
          {selectedCommentaryType === 'standard' && commentaryPicker}
        </div>
      </fieldset>
    </>
  );
};

export default Information;
